import Copy from 'apollo-react-icons/Copy';
import File from 'apollo-react-icons/File';
/**
 * Browser-based folder to tree data converter
 * Reads folder structure from FileList (from folder input) and converts to tree data
 */

/**
 * Convert FileList from folder input to tree data structure
 * @param {FileList} fileList - Files from <input type="file" webkitdirectory>
 * @param {string} basePath - Base path to prepend (e.g., '/assets/eCTD_output')
 * @returns {Array} Tree data structure
 */
export const fileListToTreeData = (fileList, basePath = '') => {
  if (!fileList || fileList.length === 0) return [];

  // Extract file paths from FileList
  const filePaths = [];
  for (let i = 0; i < fileList.length; i++) {
    const file = fileList[i];
    // Get relative path (webkitRelativePath includes folder name)
    let relativePath = file.webkitRelativePath || file.name;
    
    // Remove the root folder name from the path
    // e.g., "eCTD_output/Module 1/file.txt" -> "Module 1/file.txt"
    const parts = relativePath.split('/');
    if (parts.length > 1) {
      relativePath = parts.slice(1).join('/');
    }
    
    filePaths.push(relativePath);
  }

  return filePathsToTreeData(filePaths, basePath);
};

/**
 * Convert array of file paths to tree data structure
 * @param {Array<string>} filePaths - Array of file paths
 * @param {string} basePath - Optional base path to prepend
 * @returns {Array} Tree data structure
 */
export const filePathsToTreeData = (filePaths, basePath = '') => {
  // Build nested object structure
  const fileStructure = {};

  filePaths.forEach(filePath => {
    const parts = filePath.split('/');
    let current = fileStructure;

    parts.forEach((part, index) => {
      if (!current[part]) {
        current[part] = index === parts.length - 1 ? null : {};
      }
      if (index < parts.length - 1) {
        current = current[part];
      }
    });
  });

  // Convert to tree data format
  return directoryToTreeData(fileStructure, basePath);
};

/**
 * Convert nested directory object to tree data format
 * @param {Object} directoryStructure - Nested object representing directory
 * @param {string} basePath - Optional base path to prepend
 * @returns {Array} Tree data structure
 */
export const directoryToTreeData = (directoryStructure, basePath = '') => {
  let nodeIdCounter = 1;

  const buildTreeNode = (name, structure, currentPath = '') => {
    const relativePath = currentPath ? `${currentPath}/${name}` : name;
    const fullPath = basePath ? `${basePath}/${relativePath}` : relativePath;
    
    const node = {
      nodeId: String(nodeIdCounter++),
      label: name,
      filePath: fullPath,
    };

    // If structure is null, it's a file (leaf node)
    // If structure is an object, it's a directory with children
    if (structure !== null && typeof structure === 'object') {
      const childrenKeys = Object.keys(structure);
      if (childrenKeys.length > 0) {
        node.children = childrenKeys.map(key => 
          buildTreeNode(key, structure[key], relativePath)
        );
      }
    } else {
        // Alternate icons based on depth level
        // Count depth by number of slashes in path
        const depth = currentPath === '' ? 0 : (currentPath.match(/\//g) || []).length + 1;
        // Odd levels (1, 3, 5...) = Copy, Even levels (2, 4, 6...) = File
        node.icon = depth % 2 === 1 ? <Copy /> : <File />;
    }

    return node;
  };

  return Object.keys(directoryStructure).map(key =>
    buildTreeNode(key, directoryStructure[key])
  );
};

/**
 * Store tree data in localStorage
 * @param {Array} treeData - Tree data to store
 * @param {string} key - Storage key (default: 'ectd_tree_data')
 */
export const saveTreeDataToStorage = (treeData, key = 'ectd_tree_data') => {
  try {
    localStorage.setItem(key, JSON.stringify(treeData));
    return true;
  } catch (error) {
    console.error('Error saving tree data to storage:', error);
    return false;
  }
};

/**
 * Load tree data from localStorage
 * @param {string} key - Storage key (default: 'ectd_tree_data')
 * @returns {Array|null} Tree data or null if not found
 */
export const loadTreeDataFromStorage = (key = 'ectd_tree_data') => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error loading tree data from storage:', error);
    return null;
  }
};

/**
 * Clear tree data from localStorage
 * @param {string} key - Storage key (default: 'ectd_tree_data')
 */
export const clearTreeDataFromStorage = (key = 'ectd_tree_data') => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error clearing tree data from storage:', error);
    return false;
  }
};

/**
 * Fetch file list from index.json and convert to tree data
 * @param {string} indexUrl - URL to the index.json file (default: '/assets/eCTD_output/index.json')
 * @param {string} basePath - Base path to prepend to file paths (default: '/assets/eCTD_output')
 * @returns {Promise<Array>} Tree data structure
 */
export const fetchIndexAndConvertToTreeData = async (
  indexUrl = '/assets/eCTD_output/index.json',
  basePath = '/assets/eCTD_output'
) => {
  try {
    const response = await fetch(indexUrl);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch index file: ${response.statusText}`);
    }
    
    const data = await response.json();
    const filePaths = data.files || [];
    
    if (filePaths.length === 0) {
      console.warn('No files found in index.json');
      return [];
    }
    
    // console.log(`Loaded ${filePaths.length} files from index.json`);
    return filePathsToTreeData(filePaths, basePath);
    
  } catch (error) {
    console.error('Error fetching and converting index:', error);
    throw error;
  }
};

